import { useEffect, useState } from 'react';
import { WithTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import CurrencyPrice from '../../../../common/currency-price/currency-price';
import Spacing from '../../../../common/spacing/spacing';
import { LATINA_COUNTRY_CODE } from '../../../../constants/latina-country';
import CartIcon from '../../../../images/cart-black.svg';
import CloseImg from '../../../../images/close-2.svg';
import translation from '../../../../languages';
import formatPrice from '../../../../utils/price/format-price';
import SuccessIcon2 from './assets/24px-check-grey.svg';
import lockImage from './assets/24px-lock.svg';
import { isKushkiPayment } from '../../payment.helpers';
import KushkiInfo from '../kushki/components/kushki-info/kushki-info';
import PayButton from '../pay-button/pay-button';
import ErrorMessage from '../error-message/error-message';
import useIsMobile from '../../../../hooks/use-is-mobile';
import useHasSelectedPaymentMethod from '../../hooks/use-has-selected-payment-method';
import usePaymentOptions from '../../../../services/payment/use-payment-options';
import './cart-info.scss';

type CartInfoProps = WithTranslation & {};

const CartInfo = ({ t }: CartInfoProps) => {
    const { paymentCart, formData } = useSelector((state: RootState) => state.payment);
    const currency = useSelector((state: RootState) => state.main.currency);
    const { data: paymentOptionsData } = usePaymentOptions(currency.code);
    const countryCode = paymentOptionsData?.userCountry;
    const isMobile = useIsMobile();
    const hasSelectedPaymentMethod = useHasSelectedPaymentMethod();
    const [isKushki, setIsKushki] = useState<boolean>(false);
    const isButtonDisabled = !formData?.acceptAgb;

    useEffect(() => {
        if (!paymentOptionsData?.loaded) {
            return;
        }

        setIsKushki(isKushkiPayment(paymentOptionsData));
    }, [paymentOptionsData]);

    return (
        <div id='payment-cart-info-container'>
            <div className='payment-cart-info'>
                <div className='header-info'>
                    <img src={CartIcon} className='mr-2 cart-icon' height={20} alt='' />
                    <span>
                        {t('cart.cartItems.cart')}: {paymentCart?.cartItems.length}{' '}
                        {t('cart.cartItems.item', { count: paymentCart?.cartItems.length })}
                    </span>
                    <span className='cursor-pointer close-icon'>
                        <img src={CloseImg} alt='' />
                    </span>
                </div>
                <div className='info text-right d-flex flex-column'>
                    <div className='top d-flex flex-column'>
                        {paymentCart?.cartItems.map((cartItem) => (
                            <div
                                className='cart-values-item d-flex flex-wrap justify-content-between'
                                key={cartItem.id}
                            >
                                <div className='cart-values-item-title ellipsis'>{cartItem.material.title}</div>
                                <span className='cart-values-item-price'>
                                    <CurrencyPrice
                                        price={cartItem.finalPrice}
                                        showZeroInDecimal={
                                            countryCode === LATINA_COUNTRY_CODE.CL ||
                                            countryCode === LATINA_COUNTRY_CODE.CO
                                        }
                                    />
                                </span>
                                {cartItem.priceDescription && (
                                    <p className='price-desc lmp-secondary text-muted'>({cartItem.priceDescription})</p>
                                )}
                            </div>
                        ))}
                        <div className='text-right d-flex flex-column'>
                            <hr />
                            <div className='tsc-h4 d-flex justify-content-between sum'>
                                <span>
                                    {t('cart.cartSummary.sumDesc')}
                                    <Spacing />:
                                </span>
                                <span>
                                    <CurrencyPrice
                                        price={paymentCart?.finalPrice}
                                        showZeroInDecimal={
                                            countryCode === LATINA_COUNTRY_CODE.CL ||
                                            countryCode === LATINA_COUNTRY_CODE.CO
                                        }
                                    />
                                </span>
                            </div>
                            {Boolean(paymentCart?.voucher && paymentCart?.totalVoucherDiscount) && (
                                <span className='lmp-secondary text-muted under-price'>
                                    {t('cart.cartSummary.containsDiscount', {
                                        discount: formatPrice(paymentCart?.totalVoucherDiscount)
                                    })}
                                </span>
                            )}
                            <span className='lmp-secondary text-muted under-price'>
                                {t('cart.cartSummary.includesTax')}
                            </span>
                        </div>
                    </div>
                    <div className='bottom'>
                        {!hasSelectedPaymentMethod && isMobile && (
                            <ErrorMessage error={{ message: t('payment:payment.paymentMethodSelection.noPaymentMethodSelected') }} />
                        )}
                        <PayButton isDisabled={isButtonDisabled} />
                        <div className='d-flex justify-content-center'>
                            <div>
                                <div className='payment-container'>
                                    <div>
                                        <img alt='lock' src={lockImage} />
                                    </div>
                                    <div className='tsc-captions text-padding'>
                                        {t('cart.cartSummary.sslEncrypted')}
                                    </div>
                                </div>
                                <div className='m-1 ssl-info d-flex flex-column'>
                                    <p>
                                        <img alt='' src={SuccessIcon2} /> {t('cart.cartSummary.refundWarranty')}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {isKushki && (
                <KushkiInfo />
            )}
        </div>
    );
};

const translate = translation(['cart', 'payment']);

export default translate(CartInfo);
